import { DeleteOutlined } from '@ant-design/icons';
import { ButtonGroup, ButtonText, Col, Form, FormControl, Icon, Input, InputOption, Row, Typography } from "@visa/vds";
import { Button, Table } from "antd";
import MCSGridRevert from "components/MCSGridRevert";
import MCSOverlayLoading from 'components/MCSOverlayLoading';
import { useAuthContextUser } from "context/AuthContext";
import { GlobalAlertContext } from 'context/GlobalAlertContext';
import React, { useContext, useEffect, useState } from "react";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import { useHistory, useLocation } from 'react-router-dom';
import { EmailNotificationApi } from "services/emailNotification.service";
import VSSClientModal from "./Components/VSSClientModal";
import "./SelectedVSSClients.css";

const SelectedVSSClients = () => {
    const user = useAuthContextUser();
    const location = useLocation();
    const history = useHistory();
    const alertContext = useContext(GlobalAlertContext);
    const searchParams = new URLSearchParams(location.search);
    const eventId = searchParams.get('eventId');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [typingTimeOut, setTypingTimeout] = useState({ name: "", timeout: 0 });
    const [pull, setPull] = useState(false);
    const [loading, setLoading] = useState(false);
    const [allClients, setAllClients] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const { promiseInProgress } = usePromiseTracker();
    const [sortConfig, setSortConfig] = useState({ order: 'ascend', columnKey: 'BID' });
    const showModal = () => {
        setIsModalVisible(true);
    };
    const handleOk = () => {
        setIsModalVisible(false);
        fetchAllClients();
    };
    const handleManualSearch = (event) => {
        setSearchValue(event.target.value);
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSearchChange(event.target.value);
            clearTimeout(typingTimeOut.timeout);
        }
    }
    const handleSearchChange = async (newSearchValue) => {
        setSearchValue(newSearchValue);
        const searchedData = tableData.filter(client =>
            client.BID.toString().includes(newSearchValue) ||
            client.LicensedCompanyName.toLowerCase().includes(newSearchValue.toLowerCase()) ||
            client.LicensedCountry.toLowerCase().includes(newSearchValue.toLowerCase()) ||
            client.Region.toLowerCase().includes(newSearchValue.toLowerCase())
        );
        setFilteredData(searchedData);
    }
    const handleAutoSearch = (value) => {
        setSearchValue(value);
        if (typingTimeOut.timeout) {
            clearTimeout(typingTimeOut.timeout);
        }
        setTypingTimeout({
            name: searchValue,
            timeout: window.setTimeout(() => handleSearchChange(value), 1500),
        });
    }
    const requestDto = {
        EventId: eventId
    };
    const fetchAllClients = async () => {
        trackPromise(
            EmailNotificationApi.GetAllVssClientEmailNotification(requestDto)
                .then((res) => {
                    const transformedClients = transformModalData(res.data.result);
                    setAllClients(transformedClients);
                    const filteredClients = transformedClients.filter(client => client.HasSelectedVSSClients === true);
                    setTableData(filteredClients);
                    if (searchValue !== "") {
                        handleSearchChange(searchValue);
                    } else {
                        setFilteredData(filteredClients);
                    }
                })
                .catch((err) => alertContext.setErrorMessage("Unable to fetch list of clients.", err))
        );
    };
    const transformModalData = (clients) => {
        return clients.map(client => ({
            id: client.id,
            BID: client.bid,
            Region: client.region,
            LicensedCountry: client.licensedCountry,
            LicensedCompanyName: client.licensedCompanyName,
            HasSelectedVSSClients: client.hasSelectedVSSClients,
        }));
    };
    useEffect(() => {
        fetchAllClients();
    }, [pull]);
    const handleDelete = (id) => {
        setLoading(true);
        const originalData = [...tableData];
        const updatedData = tableData.filter(item => item.id !== id);
        setTableData(updatedData);
        const clientToDelete = tableData.find(item => item.id === id);
        const deleteDto = {
            VolUserId: user.volUserId.toString(),
            EventId: parseInt(eventId),
            Bids: [parseInt(clientToDelete.BID)]
        };
        trackPromise(
            EmailNotificationApi.DeleteSelectedVssClientsEmailNotification(deleteDto)
                .then(() => {
                    setPull(prevPull => !prevPull);
                    setLoading(false);
                })
                .catch((err) => {
                    setTableData(originalData);
                    alertContext.setErrorMessage("Failed to delete row. Please contact admin.", err);
                })
        );
    };
    const INNER_COLUMNS = [
        {
            title: 'BID',
            dataIndex: 'BID',
            key: 'BID',
            sorter: (a, b) => a.BID - b.BID,
            sortOrder: sortConfig.columnKey === 'BID' && sortConfig.order,
        },
        {
            title: 'Company',
            dataIndex: 'LicensedCompanyName',
            key: 'LicensedCompanyName',
            sorter: (a, b) => a.LicensedCompanyName.localeCompare(b.LicensedCompanyName),
            sortOrder: sortConfig.columnKey === 'LicensedCompanyName' && sortConfig.order,
        },
        {
            title: 'Country',
            dataIndex: 'LicensedCountry',
            key: 'LicensedCountry',
            sorter: (a, b) => a.LicensedCountry.localeCompare(b.LicensedCountry),
            sortOrder: sortConfig.columnKey === 'LicensedCountry' && sortConfig.order,
        },
        {
            title: 'Region',
            dataIndex: 'Region',
            key: 'Region',
            sorter: (a, b) => a.Region.localeCompare(b.Region),
            sortOrder: sortConfig.columnKey === 'Region' && sortConfig.order,
        },
        {
            title: 'Delete',
            key: 'delete',
            render: (text, record) => (
                <Button
                    type="primary"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDelete(record.id)}
                />
            ),
        }
    ];
    const handleTableChange = (pagination, filters, sorter) => {
        setSortConfig(sorter);
    };

    return (
        <>
            {(promiseInProgress || loading) && <MCSOverlayLoading />}
            <MCSGridRevert>
                <Row className="table-row" >
                    <Col>
                        <Typography className="headline" tag="h1" variant="headline">Your VSS Client List</Typography>
                    </Col>
                    {
                        <Col alignEnd textAlign="right">
                            <ButtonText onClick={showModal} className="add-btn">  <Icon name="add-alt" className="add-icon" />Add VSS Clients</ButtonText>
                        </Col>
                    }
                    <Col>
                        <Form>
                            <FormControl position="right">
                                <Input
                                    contentBeforeInput={
                                        <InputOption>
                                            <Icon
                                                name="search"
                                                resolution="low"
                                                className="search-icon"
                                            />
                                        </InputOption>
                                    }
                                    aria-label="Site-wide"
                                    useNativePlaceholder
                                    name="search"
                                    placeholder="Search BID/Company/Country/Region"
                                    value={searchValue}
                                    showHelpText
                                    onKeyPress={handleManualSearch}
                                    onChange={(e) => handleAutoSearch(e.target.value)}
                                />
                            </FormControl>
                        </Form>
                    </Col>
                </Row>
                <Row className="table-row">
                    <Table
                        dataSource={filteredData}
                        columns={INNER_COLUMNS}
                        onChange={handleTableChange}
                        rowKey="id"
                        className="client-table"
                        pagination={{ position: ["bottomCenter"] }}
                    />
                </Row>
                <Row>
                    <Col>
                        <ButtonGroup direction="right">
                            <ButtonText colorScheme="tertiary" onClick={() => history.goBack()}>Back</ButtonText>
                        </ButtonGroup>
                    </Col>
                </Row>
            </MCSGridRevert>
            <VSSClientModal
                isVisible={isModalVisible}
                handleOk={handleOk}
                allClients={allClients}
                eventId={eventId}
                volUserId={user.volUserId.toString()}
                tableData={tableData}
            />
        </>
    );
};

export default SelectedVSSClients;